/*
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * EzApi Registrating Class Extended from EzClass Template
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * TEMPLATE NOTES:
 * Avoid implementing the constructor as only the default constructor
 * (without params) is supported and assumed by EzApi during registration.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
import { detect } from 'detect-browser';
import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzNumber,
    EzString,
    EzArray,
    EzPromise
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzRegistrationState,
    EzErrorCode
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Description of what this class is for/does
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzHtml5GPS } from '/public/javascript/common/ezclocker-html5-gps.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton instance ready when true:
 *      globalThis?.ezApi?.ezclocker?.[EzHtml5GPS.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen for singleton instance ready event:
 *  Option #1:
 *      EzClass.ezWaitReady(
 *          EzHtml5GPS.ezOnReady,
 *          {listening_class}.#ezRegistrator);
 *  Option #2:
 *      {listening_class}.ezWaitDependenciesReady(
 *          {listening_class},
 *          [
 *              // ... other dependencies ...,
 *              EzHtml5GPS
 *          ],
 *          {listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton instance access:
 *  1) From within this class...: EzHtml5GPS.ezInstance
 *  2) From outside this class..: globalThis.ezApi.ezclocker.EzHtml5GPS
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzHtml5GPS extends EzClass {
    /**
     * TODO: Convert to an EzEnumeration2 object
     * @static
     * @public @function
     * Gets the available GeoPosition state enumeration
     * @returns {object}
     *  {
     *      UNKNOWN: 'UNKNOWN',
     *      DISABLED: 'DISABLED',
     *      BLOCKED: 'BLOCKED',
     *      ERROR: 'ERROR',
     *      ACTIVE: 'ACTIVE'
     *  }
     */
    static get GeoLocationState() {
        return {
            UNKNOWN: 'UNKNOWN',
            DISABLED: 'DISABLED',
            DENIED: 'DENIED',
            BLOCKED: 'BLOCKED',
            UNAVAILABLE: 'UNAVAILABLE',
            ERROR: 'ERROR',
            ACTIVE: 'ACTIVE'
        };
    }

    /**
     * @static
     * @public @function
     * Gets the GPS status from the provided gpsPosition
     * @param {object} geoLocationResponse
     * Default: null
     * @returns {string}
     */
    static determineGeoLocationState(geoLocationResponse = null) {
        if (!EzObject.isValid(geoLocationResponse)) {
            return GeoLocationState.DISABLED;
        }

        const errorCode = EzNumber.numberOrDefault(
            geoLocationResponse?.errorCode,
            EzNumber.ZERO);

        switch (errorCode) {
            case EzHtml5GPS.GeoLocationErrorCode.NO_ERROR.code:
                return EzHtml5GPS.GeoLocationState.ACTIVE;
            case EzHtml5GPS.GeoLocationErrorCode.PERMISSION_DENIED.code:
                return EzHtml5GPS.GeoLocationState.DENIED;
            case EzHtml5GPS.GeoLocationErrorCode.POSITION_UNAVAILABLE.code:
                // Returning DISABLED for POSITION_UNAVAILABLE to avoid confusion as to what it means
                return EzHtml5GPS.GeoLocationState.DISABLED;
            // return GeoLocationState.UNAVAILABLE;
            case EzHtml5GPS.GeoLocationErrorCode.TIMEOUT.code:
                // Returning DISABLED for POSITION_UNAVAILABLE to avoid confusion as to what it means
                return EzHtml5GPS.GeoLocationState.DISABLED;
            // return GeoLocationState.UNAVAILABLE;
            case EzHtml5GPS.GeoLocationErrorCode.DISABLED.code:
                return EzHtml5GPS.GeoLocationState.DISABLED;
            case EzHtml5GPS.GeoLocationErrorCode.UNKNOWN_ERROR.code:
            default:
                // Returning DISABLED for POSITION_UNAVAILABLE to avoid confusion as to what it means
                return EzHtml5GPS.GeoLocationState.DISABLED;
            //return EzHtml5GPS.GeoLocationState.ERROR;
        }
    }

    /**
     * TODO: Convert to an EzEnumeration2 object
     * @static
     * @public @readonly @property
     * Gets the error codes for GPS position errors.
     * This object contains properties for each error type,
     * each with a code, name, and messages.
     * @returns {object}
     */
    static get GeoLocationErrorCode() {
        return {
            NO_ERROR: {
                code: EzNumber.ZERO,
                name: 'NO ERROR',
                ezMessage: 'Successfully obtained the browser geo location.',
                errorMessage: '',
                gpspositionErrorResponse: null
            },
            PERMISSION_DENIED: {
                code: EzNumber.ONE,
                name: 'PERMISSION_DENIED',
                ezMessage: 'The user declied to provide their browser geo location.',
                errorMessage: 'The user declied to provide their browser geo location.',
                gpspositionErrorResponse: null
            },
            POSITION_UNAVAILABLE: {
                code: EzNumber.TWO,
                name: 'POSITION_UNAVAILABLE',
                ezMessage: 'The user\'s browser geo location is unavailable at this time.',
                errorMessage: 'The user\'s browser geo location is unavailable at this time.',
                gpspositionErrorResponse: null
            },
            TIMEOUT: {
                code: EzNumber.THREE,
                name: 'TIMEOUT',
                ezMessage: 'Timed out while attempting to obting the user\'s browser geo location.',
                errorMessage: 'Timed out while attempting to obting the user\'s browser geo location.',
                gpspositionErrorResponse: null
            },
            DISABLED: {
                code: EzNumber._401,
                name: 'DISABLED',
                ezMessage: 'The user has disabled their browser geo location features.',
                errorMessage: 'The user has disabled their browser geo location features.',
                gpspositionErrorResponse: null
            },
            UNKNOWN_ERROR: {
                code: EzNumber.MINUS_ONE,
                name: 'UNKNOWN_ERROR',
                ezMessage: 'An unexpected error occured whil attempting to obtain the user\'s browser geo location.',
                errorMessage: 'An unexpected error occured whil attempting to obtain the user\'s browser geo location.',
                gpspositionErrorResponse: null
            }
        };
    }

    /**
     * @public @function
     * Gets an object with information about the user's browser
     * @returns {object}
     * Browser information instance from detect-browser's detect() method
     */
    ezGetBrowserInfo() {
        // Detect the current browser information
        return detect();
    }

    /**
     * @public @function
     * Creates a new GPSPositionError instance from the provided template
     * @param {object} [gpsPositionErrorTemplate=null]
     * The template for the GPS position error.
     * @param {object} [gpsPositionErrorResponse=null]
     * The response object for the GPS position error.
     * @param {string} [errorMessage=null]
     * The error message for the GPS position error.
     * @param {number} [errorCode=0]
     * The error code for the GPS position error.
     * @returns {object} The created GPS position error object.
     * Example:
     *  {
     *      code: {errorCode|0},
     *      name: {gpsPositionErrorTemplate.name|'UNKNOWN_ERROR'},
     *      ezMessage: `{gpsPositionErrorTemplate.ezMessage|'An unknown error occurred.'}`,
     *      errorMessage: `{gpsPositioNErrorTemplate.errorMessage|'An unknown error occurred.'} {''|errorMessage}`,
     *      gpspositionErrorResponse: {gpsPositionErrorResponse|{code: 0, message: 'GpsPositionErrorResponse is undefined or null.'}}
     *  }
     */
    createGPSPositionError(gpsPositionErrorTemplate = null, gpsPositionErrorResponse = null, errorMessage = null, errorCode = 0) {
        let gpsPositionError = EzObject.isValid(gpsPositionErrorTemplate)
            ? gpsPositionErrorTemplate
            : EzHtml5GPS.ezInstance.POSITION_ERROR.UNKNOWN_ERROR;

        gpsPositionError.gpsPositionErrorResponse = EzObject.isValid(gpsPositionErrorResponse)
            ? gpsPositionErrorResponse
            : {
                message: 'GpsPositionErrorResponse is undefined or null.',
            };

        gpsPositionError.errorMessage = `${gpsPositionError.errorMessage} ${EzString.stringWithLengthOrEmpty(errorMessage)}`;

        gpsPositionError.code = EzNumber.isNumber(errorCode) || EzString.hasText(errorCode)
            ? errorCode
            : EzNumber.ZERO;

        gpsPositionError.browserInfo = this.ezGetBrowserInfo();

        return gpsPositionError;
    }

    /**
     * @public @function
     * Converts a GPS position error response to a corresponding error object.
     */
    fromGPSPositionError(gpsPositionErrorResponse) {
        if (!EzObject.isValid(gpsPositionErrorResponse?.code)) {
            return EzHtml5GPS.ezInstance.createGPSPositionError(
                EzHtml5GPS.ezInstance.POSITION_ERROR.UNKNOWN_ERROR,
                gpsPositionErrorResponse,
                'Provided GpsPositionErrorResponse does not have a valid code property.');
        }

        // Error code #1
        if (EzHtml5GPS.ezInstance.POSITION_ERROR.PERMISSION_DENIED.code === gpsPositionErrorResponse.code) {
            return EzHtml5GPS.ezInstance.createGPSPositionError(
                EzHtml5GPS.ezInstance.POSITION_ERROR.PERMISSION_DENIED,
                gpsPositionErrorResponse);
        }

        // Error code #2
        if (EzHtml5GPS.ezInstance.POSITION_ERROR.POSITION_UNAVAILABLE.code === gpsPositionErrorResponse.code) {
            return EzHtml5GPS.ezInstance.createGPSPositionError(
                EzHtml5GPS.ezInstance.POSITION_ERROR.POSITION_UNAVAILABLE,
                gpsPositionErrorResponse);
        }

        // Error code #3
        if (EzHtml5GPS.ezInstance.POSITION_ERROR.TIMEOUT.code === gpsPositionErrorResponse.code) {
            return EzHtml5GPS.ezInstance.createGPSPositionError(
                EzHtml5GPS.ezInstance.POSITION_ERROR.TIMEOUT,
                gpsPositionErrorResponse);
        }

        return EzHtml5GPS.ezInstance.createGPSPositionError(
            EzHtml5GPS.ezInstance.POSITION_ERROR.UNKNOWN_ERROR,
            gpsPositionErrorResponse);
    }

    /**
     * @public @method
     * Initializes this class after EzApi registration.
     * Method is called automaticlly during EzApi registration.
     * @returns {object}
     * Returns the singleton instance of this class.
     */
    ezInit() {
        /*
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * Class ezInit() function (REQUIRED)
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         * TEMPLATE NOTES:
         * 1) The ezInit function is required by EzApi to register this class.
         * 2) Place initialization code to execute once the singletong class is registered with EzApi.
         * 3) Already return the singleton instance!
         * 4) Remove this entire comment when implementing the class
         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
         */
        return EzHtml5GPS.ezInstance;
    }

    /**
     * @public @function
     * Creates a GeoLocationErrorResponse instance
     * @param {undefined|null|number} [errorCode=500]
     * @param {undefined|null|string} [errorMessage='Failed to obtain the user\'s geo location from the browser.']
     * @param {undefined|null|Error} [causedBy=null]
     * @returns {object}
     * Example result:
     *  {
     *      errorCode: {errorCode|EzErrorCode.ezEnumData(EzErrorCode.GENERAL_ERROR).errorCode},
     *      message: {errorMessage|'Failed to obtain the user's geo location from the browser.'},
     *      causedBy: {causedBy|null}
     *  }
     */
    createGeoLocationErrorResponse(errorCode = EzNumber._500, errorMessage = 'Failed to obtain the user\'s geo location from the browser.', causedBy = null) {
        return {
            errorCode: EzNumber.numberOrDefault(
                errorCode,
                EzErrorCode.ezEnumData(EzErrorCode.GENERAL_ERROR).errorCode),
            message: EzString.stringWithTextOrDefault(
                errorMessage,
                'Failed to obtain the user\'s geo location from the browser.'),
            causedBy: EzObject.assignOrNull(causedBy)
        };
    }

    /**
     * @public @function
     * Creates a GeoLocationErrorResponse instance
     * @param {geoLocation} userBrowserGeoLocation
     * @returns {object}
     * Example result:
     *  {
     *      errorCode: {errorCode|EzErrorCode.ezEnumData(EzErrorCode.GENERAL_ERROR).errorCode},
     *      message: {errorMessage|'Failed to obtain the user's geo location from the browser.'},
     *      causedBy: {causedBy|null}
     *      geoLocation: {userBrowserGeoLocation}
     *  }
     */
    createGeoLocationSuccessResponse(userBrowserGeoLocation) {
        if (!EzObject.isValid(userBrowserGeoLocation)) {
            throw new EzBadParamException(
                { userBrowserGeoLocation },
                EzHtml5GPS.ezInstance,
                EzHtml5GPS.ezInstance.createGeoLocationSuccessResponse);
        }

        return {
            errorCode: EzErrorCode.ezEnumData(EzErrorCode.NONE).errorCode,
            message: 'Successfully obtainted the user\'s geo location from the browser.',
            causedBy: null,
            userBrowserGeoLocation
        };
    }

    /**
     * @public @method
     * Gets the current geographical location of the device.
     * @param {object} options
     * Optional parameters for the geolocation request.
     * Example options object:
     *  {
     *      enableHighAccuracy: true,
     *      timeout: 30000,
     *      maximumAge: EzNumber.ZERO
     *  }
     * @returns {Promise}
     * A promise that resolves with the current position or returns an error instance
     * Example geo location response:
     *  {
     *      errorCode: 0,
     *      message: 'Successfully obtainted the user\'s geo location from the browser.',
     *      causedBy: null,
     *      geoLocation:{
     *          "timestamp":1783458043852,
     *          "coords": {
     *              "accuracy":95,
     *              "latitude":33.18420820196348,
     *              "longitude":-96.87710706474012,
     *              "altitude":null,
     *              "altitudeAccuracy":null,
     *              "heading":null,
     *              "speed":null
     *          }
     *     }
     *  }
     * Example error response:
     *  {
     *      errorCode: {errorCode|EzErrorCode.ezEnumData(EzErrorCode.GENERAL_ERROR).errorCode},
     *      message: {message|'Failed to obtain the user's geo location from the browser.'},
     *      causedBy: {causedByError|null}
     *  }
     */
    getLocation(options) {
        const generalErrorCode = EzErrorCode.ezEnumData(EzErrorCode.GENERAL_ERROR).errorCode;

        if (!window?.navigator?.geolocation?.getCurrentPosition) {
            const em = 'The current browser does not appear to support the Geo location api.';

            globalThis.ezApi.ezclocker.ezLogger.error(em);

            return reject(
                EzHtml5GPS.ezInstance.createGeoLocationErrorResponse(
                    generalErrorCode,
                    em));
        }

        const em1 = 'Could not obtain the user\'s geo location from the browser at this time.';

        return EzPromise.promise(
            (resolve, reject) => {
                try {
                    return EzObject.isValid(options)
                        ? window.navigator.geolocation.getCurrentPosition(
                            (userBrowserGeoLocation) => resolve(
                                EzHtml5GPS.ezInstance.createGeoLocationSuccessResponse(userBrowserGeoLocation)),
                            (errorCode) => {
                                const em2 = EzString.em`
                                    ${em1}
                                    Call to window.navigator.geolocation.getCurrentPosition(...) with options=${EzJson.toJson(options)}
                                    failed with errorCode=${errorCode}.`;

                                globalThis.ezApi.ezclocker.ezLogger.error(em2);

                                return reject(
                                    EzHtml5GPS.ezInstance.createGeoLocationErrorResponse(
                                        errorCode,
                                        `${em1} Error code: ${errorCode}.`,
                                        EzHtml5GPS.ezInstance.createGeoLocationErrorResponse(
                                            errorCode,
                                            em2,
                                            errorCode)));
                            },
                            options)
                        : window.navigator.geolocation.getCurrentPosition(
                            (userBrowserGeoLocation) => resolve(
                                EzHtml5GPS.ezInstance.createGeoLocationSuccessResponse(userBrowserGeoLocation)),
                            (errorCode) => {
                                const em2 = EzString.em`
                                    ${em1}
                                    Call to window.navigator.geolocation.getCurrentPosition(...) without options
                                    failed with errorCode=${errorCode}.`;

                                globalThis.ezApi.ezclocker.ezLogger.error(em2);

                                reject(
                                    EzHtml5GPS.ezInstance.createGeoLocationErrorResponse(
                                        errorCode,
                                        `${em1} Error code: ${errorCode}.`,
                                        EzHtml5GPS.ezInstance.createGeoLocationErrorResponse(
                                            errorCode,
                                            em2,
                                            errorCode)))
                            });
                } catch (err) {
                    const em = `${em1} Error reported: ${err.message}.`;

                    globalThis.ezApi.ezclocker.ezLogger.error(em, err);

                    return reject(
                        EzHtml5GPS.ezInstance.createGeoLocationErrorResponse(
                            generalErrorCode,
                            em,
                            err));
                }
            });
    }


    /**
     * @public @function
     * Gets the error message for the given GPS position error code.
     * @param {number} gpsPositionError
     * @returns {string}
     * @deprecated
     * Migrate to calling one of the following instead:
     * 1) From within this EzHtml5GPS class: EzHtml5GPS.ezInstance.fromGPSPositionError(gpsPositionError).ezMessage
     * 2) From outside this EzHtml5GPS class: globalThis.ezApi.ezclocker.ezHtml5GPS.fromGPSPositionError(gpsPositionError).ezMessage
     */
    getMessageForErrorCode(gpsPositionError) {
        return EzHtml5GPS.ezInstance.fromGPSPositionError(gpsPositionError).ezMessage;
    };

    /**
     * @public @method
     * Checks if the GPS location is within the allowed job location radius.
     * @param {object} timeEntryJobDataTagMap
     * @param {object} gpsPosition
     * @param {number} allowedJobLocationRadius
     * @returns {Resolve}
     */
    isGpsLocationWithinRadius(timeEntryJobDataTagMap, gpsPosition, allowedJobLocationRadius = 100) {
        if (EzObject.isVallid(timeEntryJobDataTagMap)) {
            throw new EzBadParamException(
                'timeEntryJobDataTagMap',
                EzHtml5GPS.ezInstance,
                EzHtml5GPS.ezInstance.EzHtml5GPS.ezInstance);
        }
        if (!EzNumber.isNumber(allowedJobLocationRadius)) {
            throw new EzBadParamException(
                'allowedJobLocationRadius',
                EzHtml5GPS.ezInstance,
                EzHtml5GPS.ezInstance.EzHtml5GPS.ezInstance);
        }

        if (!EzObject.isValid(gpsPosition)) {
            return EzPromise.resolve(false);
        }

        return EzPromise.asyncAction(
            (finished) => globalThis.ezApi.ezclocker.ezDataTagService.ezGetDataTag(timeEntryJobDataTagMap.dataTagId)
                .then(
                    (dataTagResponse) => {
                        const jobDataTag = dataTagResponse?.entity;

                        if (jobDataTag?.locationId && jobDataTag?.gpsLocationId) {
                            return globalThis.ezApi.ezclocker.ezGPSLocationService.ezGetGpsLocation(jobDataTag.gpsLocationId)
                                .then(
                                    (gpsLocationResponse) => {
                                        const gpsLocationEntity = gpsLocationResponse.gpsLocation;

                                        if (gpsLocationEntity?.latitude && gpsLocationEntity?.longitude) {
                                            const distanceBetweenPoints = globalThis.ezApi.ezclocker.ezGoogleMapsApi.calcDistance(
                                                EzNumber.strToFloat(gpsPosition.coords.latitude),
                                                EzNumber.strToFloat(gpsPosition.coords.longitude),
                                                EzNumber.strToFloat(gpsLocationEntity.latitude),
                                                EzNumber.strToFloat(gpsLocationEntity.longitude));

                                            if (EzNumber.strToFloat(distanceBetweenPoints) > EzNumber.strToFloat(allowedJobLocationRadius)) {
                                                return finished(false);
                                            }
                                        }

                                        return finished(true);
                                    });
                        }

                        return finished(true);
                    }));

    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * EzApi Registration Section
     * !! DO NOT PLACE ANY NON-EZAPI REGISTRATION CODE WITHIN OR AFTER THIS SECTION !!
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezHtml5GPS';
    }

    /**
     * @public @static @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzHtml5GPS_Ready'
        };
    }

    /**
      * @static
      * @private @field
      * Stores the singleton instance of this class that was created by and registerd with EzApi.
      * @type {EzSpinner}
      */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzHtml5GPS.ezApiName]
        ? globalThis.ezApi.ezclocker[EzHtml5GPS.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzSpinner}
     */
    static get ezInstance() {
        return EzHtml5GPS.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with EzApi.
     * @param {EzHtml5GPS} instance
     * Singleton instance of this class registered with EzApi
     */
    static set ezInstance(instance) {
        if (null != EzHtml5GPS.#ezInstance) {
            throw new Error('EzHtml5GPS\'s singleton instance is already reigstered with EzApi.');
        }

        EzHtml5GPS.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzHtml5GPS.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzHtml5GPS.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzHtml5GPS.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of the required EzApi dependencies that need to be ready before this class can register with EzApi
     * @returns {array}
     */
    static get ezRequiredEzApiDependencies() {
        return EzArray.EMPTY;
    }

    /**
     * @static
     * @public  @readonly @property
     * Gets if all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     */
    static get ezCanRegisterWithEzApi() {
        return EzHtml5GPS.ezAreAllRequiredEzApiDependenciesReady(EzHtml5GPS);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get ezIsRegisteredWithEzApi() {
        return null != EzHtml5GPS.ezInstance &&
            EzRegistrationState.REGISTERED === EzHtml5GPS.ezApiRegistrationState;
    }

    /**
     * @static
     * @public @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static ezRegisterWithEzApi() {
        if (EzHtml5GPS.ezCanRegisterWithEzApi && !EzHtml5GPS.ezIsRegisteredWithEzApi) {
            globalThis.ezApi.ezRegisterNewApi(
                EzHtml5GPS,
                EzHtml5GPS.ezApiName);
        }

        return EzRegistrationState.REGISTERED === EzHtml5GPS.ezApiRegistrationState;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzHtml5GPS.ezApiName
     *     2) Property getter EzHtml5GPS.ezEventNames
     *     3) Property getter EzHtml5GPS.ezInstance
     *     4) Property setter EzHtml5GPS.ezInstance
     *     5) Property getter EzHtml5GPS.ezApiRegistrationState
     *     6) Property setter EzHtml5GPS.ezApiRegistrationState
     *     7) Property getter EzHtml5GPS.#ezCanRegister
     *     8) Property getter EzHtml5GPS.#ezIsRegistered
     *     9) Method EzHtml5GPS.#ezRegistrator()
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
    static {
        // Option #1:
        EzHtml5GPS.ezWaitRequiredEzApiDependenciesReady(EzHtml5GPS);
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * End of EzApi Registration Section
     * !! DO NOT PLACE ANY CODE AFTER THIS SECTION !!
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
}